// ═══════════════════════════════════════════════════════════════════════════════
// PAGE PAIEMENTS STRIPE CONNECT — Cockpit Pro
// Permet au vendeur de connecter son compte Stripe pour recevoir des paiements
// Route: /cockpit-pro/paiements
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import {
  CreditCard, CheckCircle, AlertCircle, ExternalLink,
  ArrowRight, Loader2, RefreshCw, ShieldCheck, Zap,
  Euro, TrendingUp, Clock, Info
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function getToken() {
  return localStorage.getItem('ecosystem_token') || localStorage.getItem('social_token') || '';
}

// ─────────────────────────────────────────
// COMPOSANT PRINCIPAL
// ─────────────────────────────────────────
export default function PaiementsConnectPage() {
  const [searchParams] = useSearchParams();
  const stripeReturn = searchParams.get('stripe'); // "success" | "refresh"

  const [status, setStatus] = useState(null); // account status object
  const [loading, setLoading] = useState(true);
  const [onboarding, setOnboarding] = useState(false);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    fetchAccountStatus();
    if (stripeReturn === 'success') {
      setSuccessMsg('Votre compte Stripe a bien été configuré ! Vous pouvez maintenant recevoir des paiements.');
    }
  }, []);

  async function fetchAccountStatus() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/stripe-connect/account`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
      } else {
        setError("Impossible de récupérer le statut du compte.");
      }
    } catch {
      setError("Erreur de connexion au serveur.");
    } finally {
      setLoading(false);
    }
  }

  async function startOnboarding() {
    setOnboarding(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/stripe-connect/onboard`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      if (res.ok && data.onboarding_url) {
        window.location.href = data.onboarding_url;
      } else {
        setError(data.detail || "Erreur lors de l'initialisation de l'onboarding.");
      }
    } catch {
      setError("Erreur de connexion au serveur.");
    } finally {
      setOnboarding(false);
    }
  }

  async function openDashboard() {
    setDashboardLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/stripe-connect/dashboard-link`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      if (res.ok && data.dashboard_url) {
        window.open(data.dashboard_url, '_blank');
      } else {
        setError(data.detail || "Impossible d'accéder au tableau de bord.");
      }
    } catch {
      setError("Erreur de connexion au serveur.");
    } finally {
      setDashboardLoading(false);
    }
  }

  // ─────────────────────────────────────────
  // ÉTAT DE CHARGEMENT
  // ─────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-orange-500" size={32} />
      </div>
    );
  }

  const isConnected = status?.connected && status?.onboarding_complete;
  const isPending = status?.connected && !status?.onboarding_complete;

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">

      {/* Titre */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <CreditCard className="text-orange-500" size={26} />
          Paiements en ligne
        </h1>
        <p className="text-gray-500 mt-1 text-sm">
          Connectez votre compte Stripe pour recevoir les paiements de vos clients directement sur votre boutique.
        </p>
      </div>

      {/* Message succès retour Stripe */}
      {successMsg && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-xl p-4"
        >
          <CheckCircle className="text-green-600 mt-0.5 flex-shrink-0" size={20} />
          <p className="text-green-800 text-sm">{successMsg}</p>
        </motion.div>
      )}

      {/* Message d'erreur */}
      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
          <AlertCircle className="text-red-500 mt-0.5 flex-shrink-0" size={20} />
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {/* ─── CARTE STATUT ─── */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`rounded-2xl border-2 p-6 ${
          isConnected
            ? 'border-green-200 bg-green-50'
            : isPending
            ? 'border-orange-200 bg-orange-50'
            : 'border-gray-200 bg-white'
        }`}
      >
        {/* Icône + statut */}
        <div className="flex items-center gap-3 mb-4">
          {isConnected ? (
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="text-green-600" size={24} />
            </div>
          ) : isPending ? (
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
              <Clock className="text-orange-600" size={24} />
            </div>
          ) : (
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
              <CreditCard className="text-gray-400" size={24} />
            </div>
          )}
          <div>
            <div className="font-semibold text-gray-900">
              {isConnected
                ? 'Compte Stripe actif ✓'
                : isPending
                ? 'Configuration en cours…'
                : 'Compte Stripe non connecté'}
            </div>
            <div className="text-sm text-gray-500">
              {isConnected
                ? 'Vous pouvez recevoir des paiements.'
                : isPending
                ? 'Finalisez la configuration pour accepter les paiements.'
                : 'Connectez votre compte pour activer les paiements.'}
            </div>
          </div>
        </div>

        {/* Détails du compte si connecté */}
        {isConnected && status?.stripe_account_id && (
          <div className="grid grid-cols-2 gap-3 mb-4">
            <StatusChip
              ok={status.charges_enabled}
              label="Paiements activés"
            />
            <StatusChip
              ok={status.payouts_enabled}
              label="Virements activés"
            />
          </div>
        )}

        {/* Requirements si en attente */}
        {isPending && status?.requirements?.currently_due?.length > 0 && (
          <div className="bg-orange-100 rounded-lg p-3 mb-4">
            <p className="text-sm font-medium text-orange-800 mb-1">
              Informations requises par Stripe :
            </p>
            <ul className="text-xs text-orange-700 list-disc list-inside space-y-0.5">
              {status.requirements.currently_due.slice(0, 5).map((req) => (
                <li key={req}>{req}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          {isConnected ? (
            <>
              <button
                onClick={openDashboard}
                disabled={dashboardLoading}
                className="flex items-center gap-2 bg-gray-900 text-white rounded-xl px-4 py-2.5 text-sm font-medium hover:bg-gray-800 transition"
              >
                {dashboardLoading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <ExternalLink size={16} />
                )}
                Tableau de bord Stripe
              </button>
              <button
                onClick={fetchAccountStatus}
                className="flex items-center gap-2 text-gray-600 border border-gray-200 rounded-xl px-4 py-2.5 text-sm hover:bg-gray-50 transition"
              >
                <RefreshCw size={14} />
                Actualiser
              </button>
            </>
          ) : isPending ? (
            <>
              <button
                onClick={startOnboarding}
                disabled={onboarding}
                className="flex items-center gap-2 bg-orange-500 text-white rounded-xl px-5 py-2.5 text-sm font-semibold hover:bg-orange-600 transition"
              >
                {onboarding ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <ArrowRight size={16} />
                )}
                Finaliser la configuration
              </button>
              <button
                onClick={fetchAccountStatus}
                className="flex items-center gap-2 text-gray-600 border border-gray-200 rounded-xl px-4 py-2.5 text-sm hover:bg-gray-50 transition"
              >
                <RefreshCw size={14} />
                Actualiser
              </button>
            </>
          ) : (
            <button
              onClick={startOnboarding}
              disabled={onboarding}
              className="flex items-center gap-2 bg-orange-500 text-white rounded-xl px-5 py-2.5 text-sm font-semibold hover:bg-orange-600 transition w-full sm:w-auto justify-center"
            >
              {onboarding ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <CreditCard size={16} />
              )}
              Connecter mon compte Stripe
            </button>
          )}
        </div>
      </motion.div>

      {/* ─── COMMENT ÇA MARCHE ─── */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white border border-gray-100 rounded-2xl p-6"
      >
        <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Info size={18} className="text-blue-500" />
          Comment fonctionnent les paiements ?
        </h2>
        <div className="space-y-4">
          <Step
            number="1"
            icon={<CreditCard size={18} className="text-orange-500" />}
            title="Le client paie sur votre boutique"
            desc="Carte bancaire sécurisée, directement sur la page produit ou service."
          />
          <Step
            number="2"
            icon={<ShieldCheck size={18} className="text-green-500" />}
            title="Stripe sécurise la transaction"
            desc="AbracadaWorld perçoit une commission de 8%, le reste est automatiquement transféré sur votre compte."
          />
          <Step
            number="3"
            icon={<Euro size={18} className="text-blue-500" />}
            title="Vous recevez vos fonds"
            desc="Les virements vers votre compte bancaire sont effectués selon le calendrier Stripe (généralement 2 jours ouvrés)."
          />
        </div>
      </motion.div>

      {/* ─── INFOS COMMISSION ─── */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="bg-blue-50 border border-blue-100 rounded-2xl p-5"
      >
        <div className="flex items-start gap-3">
          <TrendingUp size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-blue-900 text-sm">Commission AbracadaWorld</p>
            <p className="text-blue-700 text-sm mt-0.5">
              8% sur chaque vente — Frais Stripe inclus. Exemple : pour 100€, vous recevez 92€ directement sur votre compte bancaire.
            </p>
          </div>
        </div>
      </motion.div>

      {/* ─── STRIPE CONNECT INFO ─── */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="flex items-center gap-3 text-xs text-gray-400 px-1"
      >
        <Zap size={14} />
        <span>
          Propulsé par <strong>Stripe Connect</strong> — Paiements sécurisés, certifiés PCI DSS, disponibles dans 46 pays.
        </span>
      </motion.div>
    </div>
  );
}

// ─────────────────────────────────────────
// SOUS-COMPOSANTS
// ─────────────────────────────────────────

function StatusChip({ ok, label }) {
  return (
    <div className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm ${ok ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
      {ok ? <CheckCircle size={14} /> : <Clock size={14} />}
      {label}
    </div>
  );
}

function Step({ number, icon, title, desc }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold text-gray-500">
        {number}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5">
          {icon}
          <span className="font-medium text-gray-900 text-sm">{title}</span>
        </div>
        <p className="text-xs text-gray-500">{desc}</p>
      </div>
    </div>
  );
}
